/**
 * ============================================================================
 * useCreateEquipmentMutation — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/equipment — create a new equipment record in PLANNED.
 *          The category picker drives default criticality/GAMP/cadences; the
 *          UI surfaces them but lets the user override any one before submit.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type {
  EquipmentCriticality,
  EquipmentSummary,
  GampClass,
} from './useEquipmentQuery';

export interface CreateEquipmentInput {
  equipment_tag: string;
  name: string;
  category_key: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  /** Optional overrides — when omitted the server uses category defaults. */
  criticality?: EquipmentCriticality;
  gamp_class?: GampClass;
  calibration_cadence_days?: number;
  pm_cadence_days?: number;
  requalification_cadence_days?: number;
  site_code?: string;
  location?: string;
}

export function useCreateEquipmentMutation(): UseMutationResult<
  EquipmentSummary,
  Error,
  CreateEquipmentInput
> {
  return useMutation<EquipmentSummary, Error, CreateEquipmentInput>({
    mutationFn: (input) =>
      apiFetch<EquipmentSummary>('/api/v1/equipment', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}
